import { CapacitorUpdater } from "@capgo/capacitor-updater";

// Answers from plugin.capgo.app/updates that mean "no update for this device",
// not that a download went wrong. The plugin reports them as downloadFailed.
const EXPECTED_REASONS = [
    "no_channel",
    "disable_auto_update_under_native",
    "disable_auto_update_to_major",
    "disable_auto_update_to_minor",
    "disable_auto_update_to_patch",
    "no_new_version_available",
    "on_premise_app",
    "cannot_update_via_private_channel",
    "cannot_get_bundle"
];

export interface CapgoFailureReason {
    reason: string;
    message?: string;
    expected: boolean;
}

/**
 * Classifies a reason code from Capgo. Only an exact reason code counts as
 * expected, a message that merely mentions one does not.
 */
export function classifyCapgoReason(
    reason: string | undefined,
    message?: string
): CapgoFailureReason {
    const normalized = reason?.trim() || "unknown";

    return {
        reason: normalized,
        message,
        expected: EXPECTED_REASONS.includes(normalized)
    };
}

/**
 * Explains a downloadFailed event. The event only carries the version that
 * failed. When that is a bundle other than the running one, a real download
 * failed and is never treated as expected. Otherwise the failure came from
 * the update check, and getLatest() is asked for the answer Capgo gives now.
 */
export async function fetchCapgoFailureReason(
    failedVersion?: string
): Promise<CapgoFailureReason> {
    let current: string | undefined;

    try {
        current = (await CapacitorUpdater.current())?.bundle?.version;
    } catch {
        // Keep the active version unknown.
    }

    if (failedVersion && failedVersion !== current) {
        return {
            reason: "bundle_download_failed",
            message: `bundle ${failedVersion} (running ${current ?? "unknown"})`,
            expected: false
        };
    }

    try {
        const latest = await CapacitorUpdater.getLatest();

        return classifyCapgoReason(latest.error, latest.message);
    } catch (error) {
        const message = String(error?.message ?? error).trim();

        return classifyCapgoReason(
            EXPECTED_REASONS.includes(message) ? message : "get_latest_failed",
            message
        );
    }
}
